import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { applicationRowFreshnessMs } from '@/lib/companyApplicationsList';
import {
  createApplicationListSync,
  type ApplicationListSync,
} from '@/lib/applicationListSync';
import type { Application } from '@/types';

const EMPTY_APPLICATIONS: Application[] = [];

function listCacheTouchesCompany(
  queryKey: readonly unknown[] | undefined,
  companyId: string,
): boolean {
  if (!queryKey?.length) return false;
  if (queryKey[0] === 'applications-list' && queryKey[1] === companyId) return true;
  if (queryKey[0] === 'applications' && queryKey[1] === companyId) return true;
  return false;
}

/** Cheap signature so we keep a stable array reference when cache content is unchanged. */
function listCacheSignature(apps: Application[]): string {
  if (apps.length === 0) return '0';
  let maxMs = 0;
  for (const app of apps) {
    const ms = applicationRowFreshnessMs(app);
    if (ms > maxMs) maxMs = ms;
  }
  const sample = apps.length <= 8
    ? apps.map((a) => `${a.id}:${a.state}`).join(',')
    : `${apps[0]?.id}:${apps[apps.length - 1]?.id}`;
  return `${apps.length}:${maxMs}:${sample}`;
}

function applicationsListEquivalent(a: Application[], b: Application[]): boolean {
  return listCacheSignature(a) === listCacheSignature(b);
}

/** True when at least one `applications-list` page is present for the company. */
export function hasCompanyApplicationsListCache(
  listSync: ApplicationListSync,
  companyId: string,
): boolean {
  return listSync.getFlattenedListApplications(companyId, false).length > 0;
}

/**
 * Read company applications from `applications-list` (+ legacy slice if present)
 * without mounting `useApplicationsQuery` (no progressive hydrate).
 */
export function useCompanyApplicationsListCache(
  companyId: string | undefined,
  archived = false,
): {
  applications: Application[];
  hasCache: boolean;
  listSync: ApplicationListSync;
} {
  const queryClient = useQueryClient();
  const listSync = useMemo(
    () => createApplicationListSync(queryClient),
    [queryClient],
  );
  const [applications, setApplications] = useState<Application[]>(() => {
    if (!companyId) return EMPTY_APPLICATIONS;
    return listSync.getFlattenedListApplications(companyId, archived);
  });

  useEffect(() => {
    if (!companyId) {
      setApplications(EMPTY_APPLICATIONS);
      return;
    }

    const read = () => {
      const next = listSync.getFlattenedListApplications(companyId, archived);
      setApplications((prev) => (
        applicationsListEquivalent(prev, next) ? prev : next
      ));
    };

    read();
    return queryClient.getQueryCache().subscribe((event) => {
      if (listCacheTouchesCompany(event?.query?.queryKey, companyId)) {
        read();
      }
    });
  }, [companyId, archived, queryClient, listSync]);

  return {
    applications,
    hasCache: applications.length > 0,
    listSync,
  };
}
